/** People Landing — campos del bloque "hero". Hasta el 21/09/2026 vivía en BlockEditorDialog.tsx. */
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { LANDING_VAR_NAMES, useLandingVars } from '../../landing-vars';
import { type HeroStyle, type LandingAlign } from '../../../../../../shared/types/people-landing';
import { ImageField } from '../ImageField';
import { AlignControl, HeroStylePicker } from '../controls';
import type { EditorFieldProps } from '../types';

export function HeroFields({ draft, set, imageUrls, pageSlug }: EditorFieldProps) {
  const { t } = useTranslation();
  // Incidente 08/10/2026: alguien escribió su propio nombre en el saludo ("Olá, Mariana!") y
  // la traducción automática lo copió a los 3 idiomas: toda la empresa vio "Hola, Mariana".
  // Si el texto tiene el nombre de quien edita, se sugiere la variable.
  const me = useLandingVars();
  const myName = String(me.primer_nombre ?? '').trim();
  // Palabra completa sin depender de \b (que no reconoce letras con tilde: "Ángela").
  const escaped = myName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const hasMyName = myName.length > 2 && [draft.title, draft.subtitle, draft.eyebrow].some((x) => new RegExp(`(^|[^\\p{L}])${escaped}([^\\p{L}]|$)`, 'iu').test(String(x ?? '')));
  return (
    <>
      <div className="space-y-1.5">
        <Label>{t('people_landing.editor.title', 'Título')}</Label>
        <Input value={draft.title ?? ''} onChange={(e) => set({ title: e.target.value })} />
      </div>
      <div className="space-y-1.5">
        <Label>{t('people_landing.editor.subtitle', 'Subtítulo (opcional)')}</Label>
        <Input
          value={draft.subtitle ?? ''}
          onChange={(e) => set({ subtitle: e.target.value })}
        />
      </div>
      <div className="space-y-1.5">
        <Label>{t('people_landing.editor.background', 'Imagen de fondo')}</Label>
        <ImageField
          pageSlug={pageSlug}
          imageKey={draft.imageKey}
          previewUrl={draft.imageKey ? imageUrls[draft.imageKey] : undefined}
          onChange={(k) => set({ imageKey: k })}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.cta_label', 'Botón (opcional)')}</Label>
          <Input
            value={draft.ctaLabel ?? ''}
            onChange={(e) => set({ ctaLabel: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.cta_url', 'URL del botón')}</Label>
          <Input
            value={draft.ctaUrl ?? ''}
            onChange={(e) => set({ ctaUrl: e.target.value })}
            placeholder="https://…"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.cta_secondary_label', 'Segundo botón (opcional)')}</Label>
          <Input
            value={draft.ctaSecondaryLabel ?? ''}
            onChange={(e) => set({ ctaSecondaryLabel: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.cta_secondary_url', 'URL del segundo botón')}</Label>
          <Input
            value={draft.ctaSecondaryUrl ?? ''}
            onChange={(e) => set({ ctaSecondaryUrl: e.target.value })}
            placeholder="https://…"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>{t('people_landing.editor.eyebrow', 'Etiqueta chica arriba del título (opcional)')}</Label>
        <Input
          value={draft.eyebrow ?? ''}
          maxLength={80}
          onChange={(e) => set({ eyebrow: e.target.value })}
          placeholder={t('people_landing.editor.eyebrow_placeholder', 'DÍA {{dia}} DE TU VIAJE EN WINCLAP')}
        />
      </div>
      {hasMyName && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          {t('people_landing.editor.own_name_warning', 'El texto tiene tu nombre ({{name}}): así lo ve TODA la empresa. Para que cada persona vea el suyo, escribí {{var}}.', { name: myName, var: '{{primer_nombre}}', interpolation: { escapeValue: false } })}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        {t('people_landing.editor.vars_hint', 'Variables disponibles en título, subtítulo y etiqueta:')}{' '}
        {LANDING_VAR_NAMES.map((v) => (
          <code key={v} className="mx-0.5 rounded bg-muted px-1 py-0.5 text-[11px]">{`{{${v}}}`}</code>
        ))}
      </p>
      <div className="space-y-1.5">
        <Label>{t('people_landing.editor.style', 'Estilo del fondo')}</Label>
        <HeroStylePicker
          value={(draft.style ?? 'classic') as HeroStyle}
          onChange={(st) => set({ style: st })}
          labels={{
            classic: t('people_landing.editor.style_classic', 'Clásico'),
            aurora: t('people_landing.editor.style_aurora', 'Aurora'),
            spotlight: t('people_landing.editor.style_spotlight', 'Spotlight'),
            grid: t('people_landing.editor.style_grid', 'Grilla'),
          }}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.align', 'Alineación')}</Label>
          <div>
            <AlignControl value={(draft.align ?? 'center') as LandingAlign} onChange={(a) => set({ align: a })} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.hero_size', 'Alto')}</Label>
          <div className="flex gap-1.5">
            {(['md', 'lg', 'xl'] as const).map((sz) => (
              <Button
                key={sz}
                type="button"
                size="sm"
                variant={(draft.size ?? 'md') === sz ? 'default' : 'outline'}
                onClick={() => set({ size: sz })}
              >
                {{ md: t('people_landing.editor.hero_size_md', 'Medio'), lg: t('people_landing.editor.hero_size_lg', 'Alto'), xl: t('people_landing.editor.hero_size_xl', 'Extra') }[sz]}
              </Button>
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <Label>{t('people_landing.editor.mood', 'Animación intensa')}</Label>
          <div className="flex h-8 items-center gap-2">
            <Switch
              checked={draft.mood === 'bold'}
              onCheckedChange={(v) => set({ mood: v ? 'bold' : 'calm' })}
              disabled={(draft.style ?? 'classic') === 'classic' || draft.style === 'grid'}
            />
            <span className="text-xs text-muted-foreground">
              {draft.mood === 'bold'
                ? t('people_landing.editor.mood_bold', 'Bold')
                : t('people_landing.editor.mood_calm', 'Calma')}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}
